import i18n from "@/i18n/i18n";
import type {
  AiUserPreferences,
  AssistantRequestContext,
  ConversationDetail,
  ConversationKind,
  ConversationSummary,
  PreferenceEvent,
  PreferenceField,
  StoredConversationMessage,
  TopicDraft,
  UIMessagePart,
} from "./types";
import { AURORA_CONVERSATION_KIND } from "./types";
import type { Database } from "@/integrations/supabase/types";
import { toUserFacingErrorMessage } from "./stream-parser";
import { auth } from "@lyder-talk/core/lib/auth-client";
import { db } from "@lyder-talk/core/lib/db-client";
import { apiDelete, apiGet, apiPost } from "@/lib/api-client";

const PAGE_SIZE = 20;

type AIConversationRow = Database["public"]["Tables"]["ai_conversations"]["Row"];
type AIConversationInsert = Database["public"]["Tables"]["ai_conversations"]["Insert"];
type AIMessageRow = Database["public"]["Tables"]["ai_conversation_messages"]["Row"];
type AIMessageInsert = Database["public"]["Tables"]["ai_conversation_messages"]["Insert"];
type AIMessageUpdate = Database["public"]["Tables"]["ai_conversation_messages"]["Update"];

// eslint-disable-next-line @typescript-eslint/no-explicit-any

interface AIChatErrorPayload {
  error?: string;
}

export class AIChatClientError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "AIChatClientError";
    this.status = status;
  }
}

async function getCurrentUserId(): Promise<string> {
  const {
    data: { user },
  } = await auth.getUser();

  if (!user) {
    throw new AIChatClientError("Not authenticated", 401);
  }

  return user.id;
}

/** The API base. Everything the chat needs is served from it now. */
const API_BASE = ((import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "").replace(/\/+$/, "");

async function getAccessToken(): Promise<string> {
  const {
    data: { session },
  } = await auth.getSession();

  if (!session) {
    throw new AIChatClientError("Not authenticated", 401);
  }

  return session.access_token;
}

async function parseError(response: Response): Promise<AIChatClientError> {
  try {
    const payload = (await response.json()) as AIChatErrorPayload;
    return new AIChatClientError(payload.error || "Request failed", response.status);
  } catch {
    return new AIChatClientError("Request failed", response.status);
  }
}

async function authenticatedFetch(_path: string, init?: RequestInit): Promise<Response> {
  if (!API_BASE) {
    throw new AIChatClientError("VITE_API_BASE_URL is not configured", 500);
  }

  const token = await getAccessToken();

  return fetch(`${API_BASE}/v1/chat`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init?.headers || {}),
    },
  });
}

export interface PaginatedConversations {
  conversations: ConversationSummary[];
  nextCursor: string | null;
  hasNextPage: boolean;
}

export async function listAIConversationsPaginated(
  cursor?: string | null,
  kind: ConversationKind = AURORA_CONVERSATION_KIND,
): Promise<PaginatedConversations> {
  const { data: session } = await auth.getSession();

  if (!session?.session?.user) {
    return { conversations: [], nextCursor: null, hasNextPage: false };
  }

  const userId = session.session.user.id;

  let query = db.from("ai_conversations")
    .select("id, title, created_at, updated_at")
    .eq("user_id", userId)
    .eq("kind", kind)
    .is("deleted_at", null)
    .order("updated_at", { ascending: false })
    .limit(PAGE_SIZE + 1);

  if (cursor) {
    const { data: cursorData } = await db.from("ai_conversations")
      .select("updated_at")
      .eq("id", cursor)
      .single();

    if (cursorData) {
      query = query.lt("updated_at", cursorData.updated_at);
    }
  }

  const { data, error } = await query;

  if (error) {
    console.error("Error fetching paginated conversations:", error);
    return { conversations: [], nextCursor: null, hasNextPage: false };
  }

  const rows = (data ?? []) as Pick<AIConversationRow, "id" | "title" | "created_at" | "updated_at">[];
  const hasNextPage = rows.length > PAGE_SIZE;
  const conversations = (hasNextPage ? rows.slice(0, -1) : rows) as ConversationSummary[];
  const nextCursor = hasNextPage && conversations.length > 0
    ? conversations[conversations.length - 1].id
    : null;

  return {
    conversations,
    nextCursor,
    hasNextPage,
  };
}

function rowToStoredMessage(row: AIMessageRow): StoredConversationMessage {
  // `parts` is JSONB in the DB; preserve it as-is so client-side renderers can
  // decide how to handle the various part shapes (text, data-web-search, etc.).
  // Filter out the UI-only "data-web-search-pending" placeholder parts so we
  // don't persist them on subsequent reloads.
  const parts = Array.isArray(row.parts)
    ? (row.parts as unknown as UIMessagePart[]).filter(
        (p) => p?.type !== "data-web-search-pending",
      )
    : undefined;

  return {
    id: row.id,
    role: row.role as StoredConversationMessage["role"],
    content: row.content,
    ...(parts && parts.length > 0 ? { parts } : {}),
    timestamp: row.created_at,
    modelId: row.model_id ?? undefined,
    sessionId: row.session_id ?? undefined,
  };
}

export async function getAIConversation(
  conversationId: string,
  kind: ConversationKind = AURORA_CONVERSATION_KIND,
): Promise<ConversationDetail | null> {
  const { data: conversation, error: convError } = await db.from("ai_conversations")
    .select("id, title, created_at, updated_at, user_id, deleted_at")
    .eq("id", conversationId)
    .eq("kind", kind)
    .is("deleted_at", null)
    .maybeSingle();

  if (convError) {
    throw new AIChatClientError(convError.message, 500);
  }
  if (!conversation) return null;

  const { data: messages, error: msgError } = await db.from("ai_conversation_messages")
    .select("id, role, content, parts, model_id, session_id, created_at")
    .eq("conversation_id", conversationId)
    .order("created_at", { ascending: true });

  if (msgError) {
    throw new AIChatClientError(msgError.message, 500);
  }

  const conv = conversation as AIConversationRow;
  return {
    id: conv.id,
    title: conv.title,
    created_at: conv.created_at,
    updated_at: conv.updated_at,
    user_id: conv.user_id,
    deleted_at: conv.deleted_at,
    messages: (messages ?? []).map(rowToStoredMessage),
  };
}

export async function createAIConversation(
  kind: ConversationKind = AURORA_CONVERSATION_KIND,
): Promise<string> {
  const userId = await getCurrentUserId();

  const insert: AIConversationInsert = {
    user_id: userId,
    title: "New Conversation",
    kind,
  };

  const { data, error } = await db.from("ai_conversations")
    .insert(insert)
    .select("id")
    .single();

  if (error) {
    throw new AIChatClientError(error.message, 500);
  }
  if (!data?.id) {
    throw new AIChatClientError("Failed to create conversation", 500);
  }

  return data.id;
}

export interface StreamAIConversationOptions {
  /**
   * Which system prompt the backend should use. Omitted = `topic` (Aurora's
   * topic builder), which is what every existing caller wants. The docked
   * assistant passes `assistant` to get the general language-coach prompt
   * instead of the topic-creation flow.
   */
  mode?: "topic" | "assistant";
  /** Page/question context, only meaningful in `assistant` mode. */
  context?: AssistantRequestContext;
}

export async function streamAIConversationReply(
  conversationId: string,
  messages: Array<{
    id: string;
    role: "user" | "assistant";
    content: string;
    parts: Array<{ type: "text"; text: string }>;
  }>,
  signal?: AbortSignal,
  autoTitle?: boolean,
  options?: StreamAIConversationOptions,
): Promise<Response> {
  const response = await authenticatedFetch("", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      conversationId,
      messages,
      ...(autoTitle ? { autoTitle: true } : {}),
      ...(options?.mode ? { mode: options.mode } : {}),
      ...(options?.context ? { context: options.context } : {}),
      uiLanguage: i18n.resolvedLanguage ?? i18n.language,
    }),
    signal,
  });

  if (!response.ok) {
    throw await parseError(response);
  }

  return response;
}

/**
 * Copy for a request that failed *before* the stream opened (non-2xx, or the
 * connection never established).
 *
 * `authenticatedFetch` throws `AIChatClientError` carrying the function's own
 * `error` string, and those bodies split into two very different kinds:
 *
 * - 4xx copy is written for the learner ("Rate limit exceeded…", "Payment
 *   required…"), so it is worth showing.
 * - 5xx copy is whatever the runtime threw — a missing gateway credential, an
 *   upstream stack trace. Rendering that raw is how a learner ends up reading
 *   `Cloudflare AI Gateway configuration is missing: CLOUDFLARE_ACCOUNT_ID`.
 *
 * The status decides, not the text. Without this the caller could only say
 * "Failed to send message", which tells someone who hit a quota the same thing
 * as someone whose wifi dropped.
 */
export function describeChatRequestError(error: unknown): string {
  if (error instanceof AIChatClientError) {
    if (error.status === 401 || error.status === 403) {
      return "Your session expired. Please refresh the page and sign in again.";
    }
    if (error.status >= 500) {
      return "Something went wrong while generating a reply. Please try again.";
    }
    return toUserFacingErrorMessage(error.message);
  }
  if (error instanceof TypeError) {
    // fetch() rejects with TypeError when the request never left the browser.
    return "The request was interrupted. Please check your connection and try again.";
  }
  if (error instanceof Error) {
    return toUserFacingErrorMessage(error.message);
  }
  return "Something went wrong. Please try again.";
}

export async function patchAIConversation(
  conversationId: string,
  updates: {
    messages?: StoredConversationMessage[];
    title?: string;
  },
): Promise<void> {
  if (updates.title !== undefined) {
    const { error } = await db.from("ai_conversations")
      .update({ title: updates.title })
      .eq("id", conversationId);

    if (error) {
      throw new AIChatClientError(error.message, 500);
    }
  }

  if (updates.messages) {
    // Replace semantics: delete all then bulk insert. Mirrors the previous
    // edge function's replaceMessages and is the contract the client expects.
    const { error: deleteError } = await db.from("ai_conversation_messages")
      .delete()
      .eq("conversation_id", conversationId);

    if (deleteError) {
      throw new AIChatClientError(deleteError.message, 500);
    }

    if (updates.messages.length === 0) return;

    const rows: AIMessageInsert[] = updates.messages.map((msg) => {
      // Drop UI-only placeholder parts before persisting so the next reload
      // doesn't show a "Searching..." row as a real saved message.
      const persistedParts = (msg.parts ?? []).filter(
        (p) => p?.type !== "data-web-search-pending",
      );
      const parts: UIMessagePart[] | undefined =
        persistedParts.length > 0
          ? persistedParts
          : msg.content
          ? [{ type: "text", text: msg.content }]
          : undefined;

      return {
        id: msg.id,
        conversation_id: conversationId,
        role: msg.role,
        content: msg.content ?? "",
        ...(parts ? { parts: parts as unknown as AIMessageInsert["parts"] } : {}),
        model_id: msg.modelId ?? null,
        session_id: msg.sessionId ?? null,
        ...(msg.timestamp ? { created_at: msg.timestamp } : {}),
      };
    });

    const { error: insertError } = await db.from("ai_conversation_messages")
      .insert(rows);

    if (insertError) {
      throw new AIChatClientError(insertError.message, 500);
    }
  }
}

export async function deleteAIConversation(conversationId: string): Promise<void> {
  // Soft delete via a SECURITY DEFINER RPC. A direct client-side UPDATE of
  // deleted_at is rejected by RLS: the ai_conversations view policy requires
  // `deleted_at IS NULL`, and PostgREST re-reads the updated row via RETURNING,
  // so the now-deleted row fails the policy (42501). The RPC verifies ownership
  // and bypasses the view policy. See migration 0127_ai_chat_soft_delete_rpc.sql.
  // Ownership is checked by the function; the endpoint returns 404 for a row the
  // caller cannot see, which the UI shows as "not found".
  await apiDelete(`/v1/ai/conversations/${encodeURIComponent(conversationId)}`).catch(
    (error: unknown) => {
      throw new AIChatClientError(error instanceof Error ? error.message : "Delete failed", 500);
    },
  );
}

// Sentinel type alias to ensure AIMessageUpdate is referenced and stays in sync
// with the generated Database type even when patchAIConversation only reads
// the shape indirectly via Insert. (AIMessageUpdate intentionally unused.)
// We use a type-only reference through `import type` to keep the alias active
// without producing a runtime or lint warning.
export type _AIChatMessageUpdateSentinel = AIMessageUpdate;

export async function createTopicFromDraft(
  conversationId: string,
  messageId: string,
  topicDraft: TopicDraft,
): Promise<{ success: boolean; sessionId: string; slug: string }> {
  const data = await apiPost<{ success: boolean; sessionId: string; slug: string }>(
    "/v1/topics",
    { conversationId, messageId, draft: topicDraft },
  ).catch((error: unknown) => {
    throw new AIChatClientError(error instanceof Error ? error.message : "Could not create the topic", 500);
  });

  if (!data) {
    throw new AIChatClientError("Could not create the topic", 500);
  }

  const rows = (data ?? []) as Array<{ session_id: string; slug: string }>;
  const row = rows[0];
  if (!row) {
    throw new AIChatClientError("Failed to create topic", 500);
  }

  return { success: true, sessionId: row.session_id, slug: row.slug };
}

export interface WebSearchDirectPayload {
  query: string;
  location?: string;
  language?: string;
}

export interface WebSearchDirectResponse {
  ok: boolean;
  status: "success" | "empty" | "error";
  query: string;
  results: Array<{
    position: number;
    site_name: string;
    title: string;
    snippet: string;
    url: string;
  }>;
  total_results: number;
  errorCode?: string;
  errorMessage?: string;
}

export async function retryWebSearch(
  payload: WebSearchDirectPayload,
): Promise<WebSearchDirectResponse> {
  const token = await getAccessToken();
  if (!API_BASE) {
    throw new AIChatClientError("VITE_API_BASE_URL is not configured", 500);
  }
  const response = await fetch(`${API_BASE}/v1/search`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw await parseError(response);
  }

  return (await response.json()) as WebSearchDirectResponse;
}

export async function getAIPreferences(): Promise<AiUserPreferences | null> {
  const {
    data: { user },
  } = await auth.getUser();

  if (!user) return null;

  // ai_user_preferences is not in the generated Database types yet; cast like
  // other untyped tables in this codebase (see use-saved-vocabulary).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (db as any).from("ai_user_preferences")
    .select("native_language, english_level, interests, practice_goal, provenance")
    .eq("user_id", user.id)
    .maybeSingle();

  if (error) {
    throw new AIChatClientError(error.message, 500);
  }
  if (!data) return null;

  return {
    native_language: data.native_language,
    english_level: data.english_level,
    interests: data.interests ?? [],
    practice_goal: data.practice_goal,
    provenance: data.provenance as AiUserPreferences["provenance"],
  };
}

export async function clearAIPreferences(): Promise<void> {
  const {
    data: { user },
  } = await auth.getUser();

  if (!user) return;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (db as any).from("ai_user_preferences")
    .delete()
    .eq("user_id", user.id);

  if (error) {
    throw new AIChatClientError(error.message, 500);
  }
}

/**
 * Manually upsert learner preferences. Only the provided fields are written
 * (upsert with onConflict updates just the present columns). Written fields
 * are stamped with manual provenance and logged to the activity feed.
 */
export async function updateAIPreferences(
  patch: Partial<AiUserPreferences>,
): Promise<AiUserPreferences | null> {
  const {
    data: { user },
  } = await auth.getUser();

  if (!user) return null;

  const { data: existing } = await db
    .from("ai_user_preferences")
    .select("provenance")
    .eq("user_id", user.id)
    .maybeSingle();

  const now = new Date().toISOString();
  const provenance = { ...((existing?.provenance as Record<string, unknown>) ?? {}) };
  const fields: PreferenceField[] = ["native_language", "english_level", "interests", "practice_goal"];
  for (const field of fields) {
    if (patch[field] !== undefined) {
      provenance[field] = { at: now, conversation_id: null, via: "manual" };
    }
  }

  const { data, error } = await db
    .from("ai_user_preferences")
    .upsert(
      { user_id: user.id, ...patch, provenance, updated_at: now },
      { onConflict: "user_id" },
    )
    .select("native_language, english_level, interests, practice_goal, provenance")
    .single();

  if (error) {
    throw new AIChatClientError(error.message, 500);
  }

  const savedFields = fields.filter((field) => patch[field] !== undefined);
  if (savedFields.length > 0) {
    const summaryParts: string[] = [];
    if (patch.english_level !== undefined) summaryParts.push(`${patch.english_level} level`);
    if (patch.native_language) summaryParts.push(`native language: ${patch.native_language}`);
    if (patch.interests && patch.interests.length > 0) {
      summaryParts.push(`interests: ${patch.interests.join(", ")}`);
    }
    if (patch.practice_goal) summaryParts.push(`goal: ${patch.practice_goal}`);
    if (summaryParts.length > 0) {
      void recordPreferenceEventLocal(user.id, {
        conversationId: null,
        fields: savedFields,
        summary: summaryParts.join(" · "),
        via: "manual",
      });
    }
  }

  return {
    native_language: data.native_language,
    english_level: data.english_level,
    interests: data.interests ?? [],
    practice_goal: data.practice_goal,
    provenance: data.provenance as AiUserPreferences["provenance"],
  };
}

/** Latest learning/save events for the "Recent learning" feed. */
export async function getPreferenceEvents(limit = 8): Promise<PreferenceEvent[]> {
  const {
    data: { user },
  } = await auth.getUser();

  if (!user) return [];

  // ai_preference_events is not in the generated Database types yet; cast like
  // other untyped tables in this codebase.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (db as any).from("ai_preference_events")
    .select("id, conversation_id, fields, summary, via, created_at")
    .eq("user_id", user.id)
    .order("created_at", { ascending: false })
    .limit(limit);

  if (error) {
    throw new AIChatClientError(error.message, 500);
  }
  return (data ?? []) as PreferenceEvent[];
}

async function recordPreferenceEventLocal(
  userId: string,
  input: {
    conversationId: string | null;
    fields: string[];
    summary: string;
    via: "auto" | "manual";
  },
): Promise<void> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (db as any).from("ai_preference_events").insert({
      user_id: userId,
      conversation_id: input.conversationId,
      fields: input.fields,
      summary: input.summary.slice(0, 300),
      via: input.via,
    });
  } catch (err) {
    console.warn("[ai-chat] Failed to record preference event:", err);
  }
}
